import { Box, Flex, HStack, Portal, SimpleGrid, Text } from '@chakra-ui/react';
import { Button } from '@pluxee-design-system/core';
import { SubmitButton } from '@pluxee-design-system/react-hook-form';
import VideoButton from 'common/buttons/VideoButton';
import { px, py } from 'common/layouts';
import Loader from 'common/Loader';
import { TextTitle } from 'common/typography';
import CampaignLogo from 'components/Affiliation/CampaignLogo';
import {
  ChangeLocations,
  CommonProduct,
  ProductCard,
  ProductDrawer,
  SelectedProductOption,
} from 'components/Products';
import { getPriceAndFees } from 'components/Products/PayoutCard/utils';
import { getSelectedPayout } from 'components/Products/ProductDrawer/utils';
import { WizardStep } from 'components/WizardForm/types';
import { productsSchema } from 'config';
import useDeepMemo from 'data/useDeepMemo';
import { PayoutChannel, ProductFull } from 'generated/models';
import useTranslations from 'i18n';
import { intersection, isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import gtm from 'trackers/gtm';
import { PageVideoEnum } from 'types/videos';
import scrollToTop from 'utils/scrollToTop';
import { ObjectSchema } from 'yup';
import Stepper from '../Stepper';
import { AffiliationValues, ExtraWizardProps } from '../types';
import SelectedPayoutInfo from './SelectedPayoutInfo';

export const ProductStep: WizardStep<ExtraWizardProps, AffiliationValues> = ({
  activeStep,
  campaign,
  initialValues,
  toPrev: toPrevRaw,
  toStep,
  products: allProducts,
}) => {
  const { t, tQueryKey } = useTranslations();
  const { setValue, getFieldState, watch } = useFormContext<AffiliationValues>();
  const isFieldProductsTouched = getFieldState('products').isTouched;
  const fieldProductsError = getFieldState('products').error?.message;
  const {
    append,
    fields: fieldProducts,
    remove,
    update,
  } = useFieldArray<AffiliationValues>({
    name: 'products',
  });
  const [activity, activityNames, partnerAccounts, basketItems] = watch([
    'activity',
    'activityNames',
    'partnerAccounts',
    'products',
  ]);
  const products = useMemo(
    () =>
      (allProducts || []).filter((product) => intersection(activity, product.services).length > 0),
    [activity, allProducts],
  );
  const isProductsTouched = useDeepMemo(
    () => Boolean(isFieldProductsTouched || !isEqual(initialValues.products, basketItems)),
    [initialValues.products, isFieldProductsTouched, basketItems],
  );

  // TODO: move to extra component
  const [drawerItem, setDrawerItem] = useState<ProductFull | undefined>(undefined);
  const toggleDrawerItem = useCallback(
    (item: ProductFull) => setDrawerItem((prev) => (prev === item ? undefined : item)),
    [setDrawerItem],
  );
  const resetDrawerItem = useCallback(() => setDrawerItem(undefined), [setDrawerItem]);
  const toPrev = useCallback(() => {
    gtm.trackClickProductSelectBack();
    toPrevRaw();
  }, [toPrevRaw]);
  const toNext = useCallback(() => {
    gtm.trackClickProductSelectConfirm(basketItems.length);
    scrollToTop();
  }, [basketItems.length]);

  const handleSelect = useCallback(
    (
      product: CommonProduct,
      payout: PayoutChannel,
      _?: ChangeLocations,
      __?: boolean,
      accountNumber?: string,
    ) => {
      const index = basketItems.findIndex((item) => item.productUid === product.uid);
      const tQueryProduct = tQueryKey('product_details.products.*.product', {
        where: 'product_details.products.*.product.code',
        is: product.nameKey,
      });
      const tQueryTier = tQueryKey(`${tQueryProduct}.tiers.*.tier`, {
        where: `${tQueryProduct}.tiers.*.tier.code`,
        is: payout.priceTierName,
      });

      const productName = t(`${tQueryProduct}.name`, product.name);
      const contract: SelectedProductOption = {
        productUid: product.uid,
        payoutUid: payout.payoutUid ?? undefined,
        // for tracking
        productName,
        productNameKey: product.nameKey,
        // needed by backend
        productCode: product.code,
        payoutChannelCode: payout.code ?? undefined,
        payoutFrequencyCode: payout.frequencyCode ?? undefined,
        priceTierCode: payout.priceTierCode,
        // partner account info
        partnerKey: payout.parameterCode,
      };
      setValue('partnerAccounts', {
        ...partnerAccounts,
        [String(payout.parameterCode)]: accountNumber!,
      });

      gtm.trackClickProductOfferApply(
        productName,
        getPriceAndFees(payout.priceList),
        payout.frequencyIndividual
          ? t('product_details.payoutFrequency.individual', 'Individual')
          : t(`product_details.payoutFrequency.${payout.frequencyNameKey}`, payout.frequencyName),
        t(`product_details.payouts.*.${payout.nameKey}.name`, payout.name),
        t(`${tQueryTier}.name`, payout.priceTierName),
      );

      if (index >= 0) {
        update(index, contract);
      } else {
        append(contract);
      }
      resetDrawerItem();
    },
    [append, basketItems, partnerAccounts, resetDrawerItem, setValue, t, tQueryKey, update],
  );
  const handleRemove = useCallback(
    (product: CommonProduct) => {
      const index = fieldProducts.findIndex((item) => item.productUid === product.uid);
      gtm.trackClickProductDelete(basketItems[index]?.productName);
      remove(index);
      resetDrawerItem();
    },
    [basketItems, fieldProducts, remove, resetDrawerItem],
  );

  if (!allProducts) {
    return <Loader />;
  }

  return (
    <Box width="100%" alignSelf="stretch" display="flex" flexDirection="column" minHeight="100vh">
      <Flex
        align="stretch"
        gap={10}
        direction="column"
        py={py}
        px={px}
        pb={0}
        minWidth={0}
        width="100%"
        flex={1}
      >
        <Box>
          {campaign && <CampaignLogo campaign={campaign} />}
          <VideoButton page={PageVideoEnum.AffiliationProduct} />
          <Stepper activeStep={activeStep} onStepChange={toStep} />
          <TextTitle
            mt={6}
            title={t('products.pageHeader', 'What products are the best match for your clients')}
            subtitle={t('products.subheader', '')}
          />
        </Box>
        <SimpleGrid templateColumns="repeat(auto-fit, 317px)" spacing={6}>
          {products.map((product) => {
            const basketItem = basketItems.find((item) => item.productUid === product.uid);
            const basketPayout = getSelectedPayout(product.payoutList, basketItem?.payoutUid);
            const isInBasket = Boolean(basketItem);
            const productName = t('product_details.products.*.product.name', product.name, {
              query: { where: 'product_details.products.*.product.code', is: product.nameKey },
            });
            const onShowProduct = () => {
              if (isInBasket) {
                gtm.trackClickProductEdit(productName);
              } else {
                gtm.trackClickProductSelect(
                  productName,
                  intersection(product.services, activity).map(
                    (activityId) => activityNames[activityId],
                  ),
                );
              }
              toggleDrawerItem(product);
            };

            return (
              <ProductCard
                key={product.uid}
                product={product}
                isSelected={product === drawerItem}
                isInBasket={isInBasket}
                onSelect={onShowProduct}
                withDescription={!isInBasket}
              >
                <SelectedPayoutInfo payout={basketPayout} product={product} inContract={false} />
              </ProductCard>
            );
          })}
        </SimpleGrid>
      </Flex>
      <Box position="sticky" bottom={0} margin={{ base: 0, md: '0 auto' }} justifySelf="center">
        <HStack
          boxShadow="bottom"
          justifyContent="flex-end"
          spacing={6}
          pr={{ base: 6, lg: 36 }}
          pl={6}
          py={5}
          background="semantic.surface.1"
          minWidth={{ base: '100%', md: '750px' }}
        >
          {isProductsTouched && Boolean(fieldProductsError) && (
            <Text variant="body.largeDemibold" color="semantic.text.error">
              {fieldProductsError}
            </Text>
          )}
          <Button
            variant="primaryOutlined"
            id="ProductSelectionStep_backButton"
            type="button"
            onClick={toPrev}
            background="semantic.surface.1"
          >
            {t('global_texts.buttons.back', 'Back')}
          </Button>
          <SubmitButton
            variant="primaryFilled"
            id="ProductSelectionStep__validateButton"
            onClick={toNext}
          >
            {t('global_texts.buttons.confirm', 'Confirm')}
          </SubmitButton>
        </HStack>
      </Box>
      {drawerItem && (
        <Portal>
          <ProductDrawer
            key={drawerItem.uid}
            isProductDetachable={basketItems.some((i) => i.productUid === drawerItem.uid)}
            product={drawerItem}
            partnerAccounts={partnerAccounts}
            removeText={t('products.sidebarButtons.remove', 'Remove product from selection')}
            selectedPayoutId={basketItems.find((i) => i.productUid === drawerItem.uid)?.payoutUid}
            onClose={resetDrawerItem}
            onRemove={handleRemove}
            onSelect={handleSelect}
            onTrackPayoutOfferSelect={gtm.trackClickProductOfferSelect}
          />
        </Portal>
      )}
    </Box>
  );
};

ProductStep.initial = () => ({
  products: [],
  partnerAccounts: {},
});

ProductStep.schema = () => productsSchema(true) as unknown as ObjectSchema<AffiliationValues>;

export default ProductStep;
